import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, Sparkles, FileText } from "lucide-react";
import { EASE, Eyebrow, Reveal } from "./Reveal";

const CHIPS = [
    {
        q: "What is the payroll cut-off date?",
        a: "Payroll inputs close on the 20th of every month. Any change submitted after the cut-off automatically rolls into the next month's cycle.",
        source: "Payroll Structure & CTC Components",
    },
    {
        q: "How do I submit a Concur expense report?",
        a: "Open Concur, start a new report, itemise each expense with receipts attached, and submit. Your manager approves first, then Finance clears reimbursement within five working days.",
        source: "Concur Expense Submission Guide",
    },
    {
        q: "What is covered under corporate insurance OPD?",
        a: "OPD cover includes doctor consultations, diagnostic tests, and prescribed medicines up to your annual family limit — cashless at all network clinics.",
        source: "Corporate Insurance Coverage Policy",
    },
    {
        q: "How does the new vs old tax regime comparison work?",
        a: "The assistant runs your CTC through both regimes — old regime deductions versus the new regime's lower slabs — and shows you which one saves more tax this year.",
        source: "Income Tax Regime FAQ",
    },
];

export function ChatbotSpotlight() {
    const reduce = useReducedMotion();
    const ref = useRef(null);
    const [active, setActive] = useState(null);
    const [typed, setTyped] = useState("");

    useEffect(() => {
        if (active === null) return undefined;
        const full = CHIPS[active].a;
        if (reduce) { setTyped(full); return undefined; }
        setTyped("");
        let i = 0;
        const id = setInterval(() => {
            i += 3;
            setTyped(full.slice(0, i));
            if (i >= full.length) clearInterval(id);
        }, 18);
        return () => clearInterval(id);
    }, [active, reduce]);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const yBg = useTransform(scrollYProgress, [0, 1], [reduce ? "0%" : "-12%", reduce ? "0%" : "12%"]);

    return (
        <section id="chatbot" data-testid="chatbot-spotlight-section" ref={ref} className="relative py-20 sm:py-28 scroll-mt-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="relative rounded-3xl bg-[var(--dark-panel)] border border-[var(--dark-line)] shadow-[0_0_90px_-18px_rgba(212,169,60,0.22),0_30px_80px_-30px_rgba(0,0,0,0.8)] overflow-hidden">
                    <motion.div style={{ y: yBg }} className="blueprint-grid-dark absolute -top-[15%] left-0 right-0 h-[130%] pointer-events-none" />
                    <div
                        className="absolute -right-24 -top-24 w-[380px] h-[380px] rounded-full border border-[var(--dark-accent)]/15 pointer-events-none"
                        aria-hidden="true"
                    />
                    <div
                        className="absolute -right-10 -top-10 w-[220px] h-[220px] rounded-full border border-[var(--dark-accent)]/20 pointer-events-none"
                        aria-hidden="true"
                    />

                    <div className="relative z-10 px-6 py-14 sm:px-12 sm:py-20 lg:px-16 max-w-3xl">
                        <Reveal>
                            <Eyebrow dark>04 · AI Assistant</Eyebrow>
                            <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-[1.1]">
                                Your Finance &amp; Admin Questions, Answered Instantly
                            </h2>
                            <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#EADFC6]">
                                The IDeaSpace Finance &amp; Admin Assistant is trained on IDeaS India&apos;s own internal
                                documents — payroll structure, CTC components, Concur expense submission, leave encashment
                                rules, corporate insurance coverage, travel reimbursement policy, PF and gratuity, new vs
                                old income tax regime, and more.
                            </p>
                            <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#EADFC6]">
                                Ask it in plain English. It reads the source document and gives you the answer.
                            </p>
                        </Reveal>

                        <div className="mt-9 flex flex-wrap gap-3">
                            {CHIPS.map((chip, i) => (
                                <motion.button
                                    key={chip.q}
                                    type="button"
                                    data-testid={`chatbot-question-chip-${i + 1}`}
                                    aria-pressed={active === i}
                                    onClick={() => setActive(i)}
                                    className={`min-h-[44px] inline-flex items-center gap-2 rounded-full border bg-[#241C0C]/80 backdrop-blur px-4 sm:px-5 py-2.5 text-sm text-left transition-[box-shadow,border-color,color,transform] duration-200 hover:-translate-y-[2px] hover:shadow-[0_0_26px_rgba(237,231,199,0.30)] ${
                                        active === i
                                            ? "border-[var(--dark-accent)] text-white shadow-[0_0_22px_rgba(237,231,199,0.25)]"
                                            : "border-[var(--dark-line)] text-[#F4EBD4] hover:border-[var(--dark-accent)] hover:text-white"
                                    }`}
                                    initial={{ opacity: 0, scale: reduce ? 1 : 0.9 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true, margin: "-40px" }}
                                    transition={{ duration: 0.45, delay: 0.2 + i * 0.15, ease: EASE }}
                                >
                                    <Sparkles size={14} className="shrink-0 text-[var(--dark-accent)]" />
                                    {chip.q}
                                </motion.button>
                            ))}
                        </div>

                        <AnimatePresence mode="wait">
                            {active !== null && (
                                <motion.div
                                    key={active}
                                    data-testid="chatbot-demo-panel"
                                    className="mt-8 rounded-2xl border border-[var(--dark-line)] bg-[#1A1408]/80 backdrop-blur p-5 sm:p-6"
                                    initial={{ opacity: 0, y: 14 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    transition={{ duration: 0.35, ease: EASE }}
                                >
                                    <div className="flex items-start gap-3">
                                        <span className="mono shrink-0 mt-0.5 text-[10px] tracking-[0.2em] uppercase text-[#A08C6C]">You</span>
                                        <p data-testid="chatbot-demo-question" className="text-sm text-[#F4EBD4]">{CHIPS[active].q}</p>
                                    </div>
                                    <div className="my-4 h-px bg-[var(--dark-line)]/60" />
                                    <div className="flex items-start gap-3">
                                        <span className="mono shrink-0 mt-0.5 text-[10px] tracking-[0.2em] uppercase text-[var(--dark-accent)]">AI</span>
                                        <div className="min-w-0">
                                            <p data-testid="chatbot-demo-answer" className="text-sm sm:text-base leading-relaxed text-white min-h-[4.5rem]">
                                                {typed}
                                                {typed !== CHIPS[active].a && (
                                                    <span className="inline-block w-[7px] h-4 ml-1 align-middle bg-[var(--dark-accent)]/80 animate-pulse" />
                                                )}
                                            </p>
                                            {typed === CHIPS[active].a && (
                                                <motion.p
                                                    data-testid="chatbot-demo-source"
                                                    className="mt-3 inline-flex items-center gap-2 mono text-[10px] tracking-[0.14em] uppercase text-[#A08C6C]"
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    transition={{ duration: 0.3 }}
                                                >
                                                    <FileText size={12} className="text-[var(--dark-accent)]" />
                                                    Source: {CHIPS[active].source} · Sample preview
                                                </motion.p>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <Reveal delay={0.5} className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5">
                            <a
                                href="/chatbot"
                                data-testid="chatbot-spotlight-cta"
                                className="group min-h-[48px] px-7 inline-flex items-center justify-center gap-2 rounded-full bg-[var(--dark-accent)] text-[#0A0908] font-semibold text-base hover:bg-[#F7F1DC] hover:-translate-y-[2px] active:translate-y-0 transition-[transform,background-color] duration-200"
                            >
                                Try the Finance &amp; Admin Assistant
                                <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
                            </a>
                            <span className="inline-flex items-center gap-2 mono text-[11px] tracking-[0.18em] text-[#A08C6C] uppercase">
                                <FileText size={14} className="text-[var(--dark-accent)]" />
                                Sources: IDeaS internal docs only
                            </span>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}
